import { useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";

const EMPTY_FORM = {
  name: "",
  phone: "",
  email: "",
  message: "",
  source: "",
  consent: true,
  hp: "",
  interests: [],
  age: "",
};

/** Adres kontaktowy właściwy dla regionu lokalizacji. */
function emailForArea(area) {
  return area === "jastrzebie"
    ? "jastrzebie@szkola4stars.pl"
    : "wroclaw@szkola4stars.pl";
}

export default function ContactForm({ locationObj }) {
  const { pathname } = useLocation();

  const formOpenedAt = useRef(Date.now());

  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [ok, setOk] = useState(false);
  const [err, setErr] = useState("");

  const canSend = useMemo(() => {
    if (form.name.trim().length < 2) return false;
    if (!form.phone.trim() && !form.email.trim()) return false;
    if (!form.message.trim()) return false;
    if (!form.consent) return false;
    return true;
  }, [form]);

  const onChange = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const onToggle = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.checked }));

  const toggleInterest = (label) => {
    setForm((p) => {
      const has = p.interests.includes(label);
      return {
        ...p,
        interests: has ? p.interests.filter((x) => x !== label) : [...p.interests, label],
      };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setOk(false);

    if (form.hp) return;

    if (!canSend) {
      setErr("Uzupełnij imię + wiadomość oraz telefon lub email.");
      return;
    }

    try {
      setLoading(true);

      await httpsCallable(functions, "site_submitLead")({
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        message: form.message.trim(),
        consent: true,
        interests: form.interests,
        age: String(form.age || "").trim() || null,
        locationId: locationObj?.id ?? null,
        locationName: locationObj?.name ?? null,
        sourcePath: pathname,
        source: form.source?.trim() || null,
        hp: form.hp,
        elapsedMs: Date.now() - formOpenedAt.current,
      });

      setOk(true);
      setForm(EMPTY_FORM);
      formOpenedAt.current = Date.now();
    } catch (e2) {
      console.error(e2);
      setErr(e2?.message || "Coś poszło nie tak. Spróbuj ponownie za chwilę.");
    } finally {
      setLoading(false);
    }
  };

  /** Lista zajęć prowadzonych w tej lokalizacji — z public_site/schedule. */
  const interestOptions = useMemo(
    () => (Array.isArray(locationObj?.interests) ? locationObj.interests : []),
    [locationObj]
  );

  const contactEmail = emailForArea(locationObj?.area);

  return (
    <section style={({display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-start", width: "92%", maxWidth: "1200px", margin: "60px auto 80px", gap: "14px"})}>
      <div style={({width: "100%", display: "flex", flexDirection: "column", gap: "4px"})}>
        <h2 style={({margin: "0px", fontSize: "22px", fontWeight: "900", color: "rgba(0,0,0,0.9)"})}>Formularz kontaktowy</h2>
        <p style={({margin: "0px", fontSize: "12px", fontWeight: "600", color: "rgba(0,0,0,0.6)", marginTop: "-5px", marginLeft: "3px"})}>
          Napisz - wrócimy z odpowiedzią najszybciej jak się da.
        </p>
      </div>

      <div style={({width: "100%", display: "flex", flexDirection: "row", flexWrap: "wrap", gap: "12px"})}>
        <div style={({flex: "1 1 260px", backgroundColor: "rgba(255,255,255,0.92)", border: "1px solid rgba(0,0,0,0.08)", borderRadius: "18px", padding: "14px 16px", boxShadow: "0 8px 24px rgba(0,0,0,0.06)"})}>
          <div style={({fontSize: "12px", fontWeight: "800", color: "rgba(0,0,0,0.65)"})}>Telefon</div>
          <div style={({fontSize: "14px", fontWeight: "900", color: "rgba(0,0,0,0.9)"})}>
            <a href="tel:+48505376552" style={({color: "inherit", textDecoration: "none"})}>+48 505 376 552</a>
          </div>
        </div>

        <div style={({flex: "1 1 260px", backgroundColor: "rgba(255,255,255,0.92)", border: "1px solid rgba(0,0,0,0.08)", borderRadius: "18px", padding: "14px 16px", boxShadow: "0 8px 24px rgba(0,0,0,0.06)"})}>
          <div style={({fontSize: "12px", fontWeight: "800", color: "rgba(0,0,0,0.65)"})}>Email</div>
          <div style={({fontSize: "13px", fontWeight: "900", color: "rgba(0,0,0,0.9)"})}>
            <a href={`mailto:${contactEmail}`} style={({color: "inherit", textDecoration: "none"})}>{contactEmail}</a>
          </div>
        </div>

        <div style={({flex: "1 1 260px", backgroundColor: "rgba(255,255,255,0.92)", border: "1px solid rgba(0,0,0,0.08)", borderRadius: "18px", padding: "14px 16px", boxShadow: "0 8px 24px rgba(0,0,0,0.06)"})}>
          <div style={({fontSize: "12px", fontWeight: "800", color: "rgba(0,0,0,0.65)"})}>Lokalizacja</div>
          <div style={({fontSize: "14px", fontWeight: "900", color: "rgba(0,0,0,0.9)"})}>{locationObj?.name ?? "—"}</div>
        </div>
      </div>

      <form onSubmit={submit} style={({width: "100%", backgroundColor: "rgba(255,255,255,0.92)", border: "1px solid rgba(0,0,0,0.08)", borderRadius: "18px", padding: "16px", boxShadow: "0 8px 24px rgba(0,0,0,0.06)", display: "flex", flexDirection: "column", gap: "10px"})}>
        <input value={form.hp} onChange={onChange("hp")} style={({display: "none"})} tabIndex={-1} autoComplete="off" aria-hidden="true" />

        <input value={form.name} onChange={onChange("name")} placeholder="Imię i nazwisko" autoComplete="name" style={({border: "1px solid rgba(0,0,0,0.12)", borderRadius: "14px", padding: "12px 12px", outline: "none", fontSize: "13px"})} />

        <div style={({display: "flex", gap: "10px", flexWrap: "wrap"})}>
          <input type="tel" inputMode="tel" autoComplete="tel" value={form.phone} onChange={onChange("phone")} placeholder="Telefon" style={({flex: "1 1 220px", border: "1px solid rgba(0,0,0,0.12)", borderRadius: "14px", padding: "12px 12px", outline: "none", fontSize: "13px"})} />
          <input type="email" autoComplete="email" value={form.email} onChange={onChange("email")} placeholder="Email (opcjonalnie)" style={({flex: "1 1 220px", border: "1px solid rgba(0,0,0,0.12)", borderRadius: "14px", padding: "12px 12px", outline: "none", fontSize: "13px"})} />
        </div>

        <div style={({display: "flex", gap: "10px", flexWrap: "wrap"})}>
          <input
            value={form.age}
            onChange={onChange("age")}
            placeholder="Wiek uczestnika (np. 7 lub 16+)"
            inputMode="numeric"
            type="text"
            style={({flex: "1 1 220px", border: "1px solid rgba(0,0,0,0.12)", borderRadius: "14px", padding: "12px 12px", outline: "none", fontSize: "13px"})}
          />
        </div>

        {interestOptions.length > 0 ? (
          <div style={({display: "flex", flexDirection: "column", gap: "8px"})}>
            <div style={({fontSize: "12px", fontWeight: "800", color: "rgba(0,0,0,0.7)"})}>Jakie zajęcia Cię interesują?</div>

            <div style={({display: "flex", flexDirection: "row", flexWrap: "wrap", gap: "10px"})}>
              {interestOptions.map((label) => {
                const checked = form.interests.includes(label);
                return (
                  <label key={label} style={({display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", border: "1px solid rgba(0,0,0,0.12)", backgroundColor: checked ? "rgba(30,30,30,0.06)" : "rgba(255,255,255,0.95)", borderRadius: "999px", padding: "8px 12px"})}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleInterest(label)}
                      style={({margin: "0px"})}
                    />
                    <span style={({fontSize: "12px", fontWeight: "800", color: "rgba(0,0,0,0.75)"})}>{label}</span>
                  </label>
                );
              })}
            </div>
          </div>
        ) : null}

        <textarea value={form.message} onChange={onChange("message")} placeholder="Wiadomość" rows={4} style={({border: "1px solid rgba(0,0,0,0.12)", borderRadius: "14px", padding: "12px 12px", outline: "none", fontSize: "13px", resize: "none"})} />

        <input value={form.source} onChange={onChange("source")} placeholder="Skąd się o nas dowiedziałaś/dowiedziałeś?" style={({border: "1px solid rgba(0,0,0,0.12)", borderRadius: "14px", padding: "12px 12px", outline: "none", fontSize: "13px"})} />

        <label style={({display: "flex", alignItems: "center", gap: "10px", fontSize: "12px", color: "rgba(0,0,0,0.7)", fontWeight: "600"})}>
          <input type="checkbox" checked={form.consent} onChange={onToggle("consent")} />
          Wyrażam zgodę na kontakt w sprawie zajęć.
        </label>

        {err ? <div role="alert" style={({color: "#d0142d", fontSize: "12px", fontWeight: "800"})}>{err}</div> : null}
        {ok ? <div role="status" style={({color: "rgba(0,0,0,0.75)", fontSize: "12px", fontWeight: "800"})}>Wysłane ✅ Odezwiemy się wkrótce.</div> : null}

        <button type="submit" disabled={!canSend || loading} style={({border: "none", outline: "none", cursor: loading ? "default" : "pointer", borderRadius: "999px", padding: "12px 14px", fontSize: "12px", fontWeight: "900", backgroundColor: locationObj?.color ?? "#d0142d", color: "rgb(250,250,250)", opacity: !canSend || loading ? "0.7" : "1", marginTop: "20px"})}>
          {loading ? "Wysyłanie..." : "Wyślij wiadomość"}
        </button>
      </form>
    </section>
  );
}